import React from "react";
import { DefaultButton, Dropdown, PrimaryButton, Stack } from "@fluentui/react";
import { Formik } from "formik";
import {
  addAuthorizedContacts,
  updateAuthorizedContacts,
} from "../../../services/customers/CustomerService";
import { IAuthorizedContacts } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";

const AcSchema = yup.object().shape({
  name: yup.string().required("required"),
  email: yup.string().email().required("required"),
  phone: yup.string().required("required"),
  datacenterId: yup.string().required("required"),
});

export interface ManageAcDialogProps {
  data?: IAuthorizedContacts;
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
  allDatacenterDropdown: any;
}

const ManageAcDialog: React.FC<ManageAcDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  customerId,
  allDatacenterDropdown,
}) => {
  //state
  const [state, setState] = React.useState({
    authorizedContacts: {
      name: "",
      email: "",
      phone: "",
      datacenterId: "",
      customerId: customerId,
      source: 'manual',
      ...data,
    },
    isLoading: false,
  } as {
    authorizedContacts: IAuthorizedContacts;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);

  const closeAddDialog = (data?: IAuthorizedContacts) => {
    handleDialogClose(data);
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.authorizedContacts._id
            ? "Update Authorized Contact"
            : "Add Authorized Contact"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={state.authorizedContacts}
            validationSchema={AcSchema}
            onSubmit={async (
              values: IAuthorizedContacts,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.authorizedContacts._id) {
                result = await updateAuthorizedContacts(obj);
              } else {
                result = await addAuthorizedContacts(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
              } else {
                showToast(
                  result?.message ||
                  `Error while ${state.authorizedContacts._id ? "updating" : "adding"
                  } authorized contact`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap} className="pt-16">
                  <Stack.Item>
                    <CustomTextField name={"name"} label={"Name"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"email"} label={"Email"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"phone"} label={"Phone"} />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={allDatacenterDropdown}
                      selectedKey={values.datacenterId}
                      onChange={(e, item) => {
                        setFieldValue("datacenterId", item?.key);
                      }}
                      label="Datacenter"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.datacenterId &&
                          touched.datacenterId &&
                          errors.datacenterId) as string
                      }
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.authorizedContacts._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageAcDialog;
